import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'framer-motion'
import { FileText, Mail, Phone, Building2, Shield, Scale } from 'lucide-react'

export default function Impressum() {
  const heroRef = useRef<HTMLDivElement>(null)
  const { scrollYProgress } = useScroll()
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])

  return (
    <div className="bg-[#0a0a0a]">
      
      {/* ════════════════════════════════════════════════════════════
          HERO SECTION
      ════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative h-[50vh] min-h-[400px] max-h-[550px] w-full overflow-hidden">
        {/* Image Background */}
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/heros/hero_bilder.jpg"
            alt="Impressum"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(0.3)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-[#0a0a0a] to-[#0a0a0a]" />
        </div>
        <div className="absolute top-0 left-1/4 w-[400px] h-[400px] bg-brand-800/5 rounded-full blur-[120px] z-0" />
        
        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 h-full flex flex-col justify-center items-center text-center px-6 pt-16"
        >
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-4"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-xl border border-white/10 rounded-full">
              <FileText className="w-3 h-3 text-brand-400" />
              <span className="text-white/70 text-xs uppercase tracking-[0.25em]">Rechtliches</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-light tracking-tight leading-[1.1]"
          >
            Impressum
          </motion.h1>
        </motion.div>
      </section>

      {/* ════════════════════════════════════════════════════════════
          CONTENT
      ════════════════════════════════════════════════════════════ */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6 relative z-10 space-y-6">
          
          {/* Angaben gemäß § 5 TMG */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-6 md:p-8 bg-white/[0.02] border border-white/5 rounded-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <Building2 className="w-5 h-5 text-brand-400" />
              <h2 className="font-serif text-xl text-white">Angaben gemäß § 5 TMG</h2>
            </div>
            <div className="h-px w-12 bg-brand-800 mb-4" />
            <p className="text-white font-medium mb-2">HM Betriebsgesellschaft mbH</p>
            <p className="text-white/50">Hückelsmaystr. 70<br />47804 Krefeld</p>
          </motion.div>

          {/* Vertreten durch */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="p-6 md:p-8 bg-white/[0.02] border border-white/5 rounded-2xl"
          >
            <h3 className="font-serif text-lg text-white mb-3">Vertreten durch:</h3>
            <div className="h-px w-10 bg-brand-800 mb-3" />
            <p className="text-white/50">Geschäftsführer: Felix Reese</p>
          </motion.div>

          {/* Kontakt */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="p-6 md:p-8 bg-white/[0.02] border border-white/5 rounded-2xl"
          >
            <h3 className="font-serif text-lg text-white mb-4">Kontakt:</h3>
            <div className="h-px w-10 bg-brand-800 mb-4" />
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-brand-400" />
                <a href="tel:+4915755537615" className="text-white/50 hover:text-brand-400 transition-colors">
                  +49 (0) 1575 5537615
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-brand-400" />
                <a href="mailto:info@hueckels-may.de" className="text-white/50 hover:text-brand-400 transition-colors">
                  info@hueckels-may.de
                </a>
              </div>
            </div>
          </motion.div>

          {/* Registereintrag */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="p-6 md:p-8 bg-white/[0.02] border border-white/5 rounded-2xl"
          >
            <h3 className="font-serif text-lg text-white mb-3">Registereintrag:</h3>
            <div className="h-px w-10 bg-brand-800 mb-3" />
            <div className="text-white/50 space-y-1">
              <p>Eintragung im Handelsregister.</p>
              <p>Registergericht: Köln</p>
              <p>Registernummer: HRB 112217</p>
            </div>
          </motion.div>

          {/* Umsatzsteuer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
            className="p-6 md:p-8 bg-white/[0.02] border border-white/5 rounded-2xl"
          >
            <h3 className="font-serif text-lg text-white mb-3">Umsatzsteuer:</h3>
            <div className="h-px w-10 bg-brand-800 mb-3" />
            <p className="text-white/50">
              Umsatzsteuer-Identifikationsnummer gemäß §27 a Umsatzsteuergesetz: DE XXXX
            </p>
          </motion.div>

          {/* Aufsichtsbehörde */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
            className="p-6 md:p-8 bg-white/[0.02] border border-white/5 rounded-2xl"
          >
            <h3 className="font-serif text-lg text-white mb-3">Aufsichtsbehörde:</h3>
            <div className="h-px w-10 bg-brand-800 mb-3" />
            <div className="text-white/50 space-y-2">
              <p>Stadtverwaltung Krefeld</p>
              <p>Von-der-Leyen-Platz 1<br />47798 Krefeld</p>
              <a href="https://www.krefeld.de/" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:text-brand-300 transition-colors inline-block mt-2">
                https://www.krefeld.de/
              </a>
            </div>
          </motion.div>

          {/* Betriebshaftpflichtversicherung */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            className="p-6 md:p-8 bg-white/[0.02] border border-white/5 rounded-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-5 h-5 text-brand-400" />
              <h3 className="font-serif text-lg text-white">Angaben zur Betriebshaftpflichtversicherung</h3>
            </div>
            <div className="h-px w-12 bg-brand-800 mb-4" />
            <div className="text-white/50 space-y-4">
              <div>
                <p className="text-brand-400/60 text-xs uppercase tracking-wider mb-1">Name und Sitz des Versicherers:</p>
                <p>Zurich Versicherungs AG<br />Platz der Einheit 2<br />60327 Frankfurt am Main</p>
              </div>
              <div>
                <p className="text-brand-400/60 text-xs uppercase tracking-wider mb-1">Geltungsraum der Versicherung:</p>
                <p>Betriebsgrundstück</p>
              </div>
            </div>
          </motion.div>

          {/* Verantwortlich für den Inhalt */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.7 }}
            className="p-6 md:p-8 bg-white/[0.02] border border-white/5 rounded-2xl"
          >
            <h3 className="font-serif text-lg text-white mb-3">Verantwortlich für den Inhalt nach § 55 Abs. 2 RStV:</h3>
            <div className="h-px w-10 bg-brand-800 mb-3" />
            <div className="text-white/50">
              <p className="text-white font-medium mb-1">HM Betriebsgesellschaft mbH</p>
              <p>Hückelsmaystr. 70<br />47804 Krefeld</p>
            </div>
          </motion.div>

          {/* Streitschlichtung */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
            className="p-6 md:p-8 bg-white/[0.02] border border-white/5 rounded-2xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <Scale className="w-5 h-5 text-brand-400" />
              <h3 className="font-serif text-lg text-white">Streitschlichtung</h3>
            </div>
            <div className="h-px w-12 bg-brand-800 mb-4" />
            <div className="text-white/50 space-y-3 text-sm leading-relaxed">
              <p>
                Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:{' '}
                <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer" className="text-brand-400 hover:text-brand-300 underline transition-colors">
                  https://ec.europa.eu/consumers/odr
                </a>
              </p>
              <p>Unsere E-Mail-Adresse finden Sie oben im Impressum.</p>
              <p>
                Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
              </p>
            </div>
          </motion.div>

        </div>
      </section>

    </div>
  )
}
